import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Typography,
  Grid,
  Paper,
  Box,
  Badge,
  Chip,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getServicesApi,
  addOrUpdateService,
  deleteService,
} from "../../apis/apisList/userApi";

const ServiceDialog = ({
  open,
  onClose,
  fetchTrigger,
  setFetchTrigger,
  planId,
}) => {
  const { id: projectId } = useParams();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentService, setCurrentService] = useState({
    _id: null,
    name: "",
    description: "",
    price: "",
    quantity: 1,
  });
  const [errors, setErrors] = useState({});

  // Fetch services for this project + plan
  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await getServicesApi(projectId, planId);
      setServices(res || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  console.log(">>services>>>", services);

  useEffect(() => {
    if (open) {
      fetchServices();
    }
  }, [open, fetchTrigger]);

  // Validate inputs
  const validate = () => {
    let errs = {};
    if (!currentService.name) errs.name = "Name is required";
    if (!currentService.price) errs.price = "Price is required";
    if (currentService.price < 0) errs.price = "Price must be positive";
    // if (!currentService.quantity || currentService.quantity < 1)
    //   errs.quantity = "Quantity must be at least 1";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Add or update service
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        projectId,
        planId,
        name: currentService.name,
        description: currentService.description,
        price: Number(currentService.price),
        quantity: Number(currentService.quantity),
        id: currentService._id,
      };

      await addOrUpdateService(payload);
      toast.success(
        currentService._id
          ? "Service updated successfully"
          : "Service added successfully"
      );

      setCurrentService({
        _id: null,
        name: "",
        description: "",
        price: "",
        quantity: 1,
      });
      setFetchTrigger((prev) => !prev); // trigger refresh
    } catch (error) {
      toast.error("Failed to save service");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Edit a service
  const handleEdit = (service) => {
    setCurrentService({
      _id: service._id,
      name: service.name,
      description: service.description,
      price: service.price,
      quantity: service.quantity,
    });
  };

  // Delete a service
  const handleDelete = async (_id) => {
    if (!window.confirm("Are you sure you want to delete this service?"))
      return;

    try {
      await deleteService(_id);
      toast.success("Service deleted successfully");
      setFetchTrigger((prev) => !prev);
    } catch (error) {
      toast.error("Failed to delete service");
      console.error(error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {currentService._id ? "Edit Service" : "Add Service"}
      </DialogTitle>
      <DialogContent>
        {/* Add/Edit Form */}
        <Box component="form" onSubmit={handleSave} mb={2}>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Service Name"
                value={currentService.name}
                onChange={(e) =>
                  setCurrentService({ ...currentService, name: e.target.value })
                }
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={currentService.price}
                onChange={(e) =>
                  setCurrentService({
                    ...currentService,
                    price: e.target.value,
                  })
                }
                error={!!errors.price}
                helperText={errors.price}
              />
            </Grid>
            {/* <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={currentService.quantity}
                onChange={(e) =>
                  setCurrentService({
                    ...currentService,
                    quantity: e.target.value,
                  })
                }
                error={!!errors.quantity}
                helperText={errors.quantity}
              />
            </Grid> */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description"
                value={currentService.description}
                onChange={(e) =>
                  setCurrentService({
                    ...currentService,
                    description: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>
        </Box>

        {/* Service List */}
        <Typography variant="h6" mt={2} mb={1}>
          Existing Services
        </Typography>
        {loading ? (
          <CircularProgress size={30} />
        ) : services.length === 0 ? (
          <Typography>No services added yet.</Typography>
        ) : (
          <Grid container spacing={1}>
            {services.map((service) => (
              <Grid item xs={12} key={service._id}>
                <Paper
                  elevation={1}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                  }}
                >
                  <Box>
                    <Typography>
                      <strong>{service.name}</strong> - {service.description}
                    </Typography>
                    <Typography variant="body2">
                      Price: ${service.price}{" "}
                      {/* | Quantity: {service.quantity} */}
                    </Typography>
                  </Box>
                  {service.isAdditional ? (
                    <Box>
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(service)}
                        size="small"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(service._id)}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  ) : (
                    <Chip
                      label="Default"
                      color="success" /* variant="outlined"  */
                    />
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {saving ? "Saving..." : currentService._id ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceDialog;
